import React, { useCallback, useRef, useState } from 'react';
import Select from 'react-select';

import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { jobFormSelectStyles } from '../ui/sharedStyles';
import palette from './palette';

const styles = `
  .saved-jobs-head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    column-gap: 12px;
  }
  .saved-jobs-clear {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 0;
    border: 0;
    background: none;
    color: ${palette.muted};
    font: inherit;
    font-size: 12.5px;
    cursor: pointer;
  }
  .saved-jobs-clear:hover { color: ${palette.danger}; }
  .saved-jobs-clear:focus-visible {
    outline: 2px solid ${palette.brandBlue};
    outline-offset: 2px;
  }
`;

export interface JobSummary {
  id: string;
  title?: string | null;
}

interface JobOption {
  value: string;
  label: string;
}

interface SavedJobsSelectorProps {
  heading: string;
  selectId: string;
  // Shown in the order given.
  jobs: JobSummary[];
  selectedId?: string;
  onChange: (id: string) => void;
  onClear: () => void;
}

function SavedJobsSelector({
  heading,
  selectId,
  jobs,
  selectedId,
  onChange,
  onClear,
}: SavedJobsSelectorProps): JSX.Element {
  const options: JobOption[] = jobs.map(function toOption(job) {
    return { value: job.id, label: `${job.title || 'Untitled'} (${job.id})` };
  });
  const selectedOption =
    options.find(function isSelected(option) {
      return option.value === selectedId;
    }) ?? null;

  const [isConfirming, setIsConfirming] = useState(false);
  const clearButtonRef = useRef<HTMLButtonElement>(null);

  // Focus Cancel when the dialog opens, so a stray Enter does not clear.
  const focusCancel = useCallback((footer: HTMLSpanElement | null) => {
    footer?.querySelector<HTMLButtonElement>('button')?.focus();
  }, []);

  function handleCancel() {
    setIsConfirming(false);
    clearButtonRef.current?.focus();
  }

  function handleConfirm() {
    setIsConfirming(false);
    onClear();
  }

  function handleChange(option: JobOption | null) {
    if (option) {
      onChange(option.value);
    }
  }

  const jobCount = jobs.length === 1 ? '1 job' : `${jobs.length} jobs`;

  return (
    // The selected job's status alert follows directly on both pages.
    <div style={{ marginBottom: 15 }}>
      <style>{styles}</style>
      <div className="saved-jobs-head">
        <h3>{heading}</h3>
        <button
          ref={clearButtonRef}
          type="button"
          className="saved-jobs-clear"
          onClick={() => setIsConfirming(true)}
        >
          Clear all jobs
          <i className="fa fa-trash-o" aria-hidden="true" />
        </button>
      </div>
      <Select
        id={selectId}
        value={selectedOption}
        onChange={handleChange}
        options={options}
        placeholder="Select a job"
        isClearable={false}
        className="job-selector"
        styles={jobFormSelectStyles}
      />
      <Modal show={isConfirming} onHide={handleCancel}>
        <Modal.Header closeButton>
          <Modal.Title>Clear all jobs?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Clearing only empties this browser's list, so say what survives. */}
          <p style={{ margin: 0 }}>
            {`This removes ${jobCount} from the list in this browser. Their results stay available at their links.`}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <span ref={focusCancel}>
            <Button onClick={handleCancel} style={{ marginRight: 10 }}>
              Cancel
            </Button>
            <Button bsStyle="danger" onClick={handleConfirm}>
              Clear
            </Button>
          </span>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SavedJobsSelector;
